import React from 'react';
import { useTranslation } from 'react-i18next';
import { LabelInput } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import FormLayout from '@/components/layout/FormLayout';
import { PermissionGuard } from '@/components/layout';
import type { EmployeeProfile } from '@/api/domains/employee';
import { normalizeSelectToNumber } from '@/utils/formHelpers';


interface AddressDataFormProps {
  profile: EmployeeProfile | null;
  regionOptions: { value: string; label: string }[];
  communeOptions: { value: string; label: string }[];
  workRegionOptions: { value: string; label: string }[];
  workCommuneOptions: { value: string; label: string }[];
  onCancel: () => void;
  addressForm: {
    register: any;
    handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    setValue: any;
    watch: any;
    errors: any;
    isSubmitting: boolean;
  };
}

export const EmployeeProfileAddressDataForm: React.FC<AddressDataFormProps> = ({
  profile,
  regionOptions,
  communeOptions,
  workRegionOptions = [],
  workCommuneOptions = [],
  onCancel,
  addressForm,
}) => {
  const { t: translate } = useTranslation('employee');
  const { register, handleSubmit, setValue, watch, errors, isSubmitting } = addressForm;

  // Watch for controlled inputs
  const regionId = watch('regionId');
  const communeId = watch('communeId');
  const workRegionId = watch('workRegionId');
  const workCommuneId = watch('workCommuneId');

  if (!profile) return null;

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      <FormLayout.Section title={translate('addressForm.personalSection')}>
        {/* Row 1: Región, Comuna */}
        <Selector
          label={translate('addressForm.region')}
          value={regionId ? String(regionId) : undefined}
          onChange={(value) => {
            const numericValue = normalizeSelectToNumber(value);
            setValue('regionId', numericValue || null, { shouldDirty: true });
            // Reset commune when region changes
            if (!numericValue) {
              setValue('communeId', null, { shouldDirty: true });
            }
          }}
          options={regionOptions}
          placeholder={translate('addressForm.regionPlaceholder')}
          color={errors.regionId ? 'error' : 'default'}
          supportingText={errors.regionId?.message}
        />
        <Selector
          label={translate('addressForm.commune')}
          value={communeId ? String(communeId) : undefined}
          onChange={(value) => {
            const numericValue = normalizeSelectToNumber(value);
            setValue('communeId', numericValue || null, { shouldDirty: true });
          }}
          options={communeOptions}
          placeholder={translate('addressForm.communePlaceholder')}
          disabled={!regionId}
          color={errors.communeId ? 'error' : 'default'}
          supportingText={errors.communeId?.message}
        />

        {/* Row 2: Dirección (spans 4 columns) */}
        <LabelInput
          className="md:col-span-4"
          label={translate('addressForm.address')}
          registration={register('address')}
          color={errors.address ? 'error' : undefined}
          supportingText={errors.address?.message}
        />
      </FormLayout.Section>

      <FormLayout.Section title={translate('addressForm.workSection')}>
        {/* Row 1: Región, Comuna */}
        <Selector
          label={translate('addressForm.region')}
          value={workRegionId ? String(workRegionId) : undefined}
          onChange={(value) => {
            const numericValue = normalizeSelectToNumber(value);
            setValue('workRegionId', numericValue || null, { shouldDirty: true });
            // Reset commune when region changes
            if (!numericValue) {
              setValue('workCommuneId', null, { shouldDirty: true });
            }
          }}
          options={workRegionOptions}
          placeholder={translate('addressForm.regionPlaceholder')}
          color={errors.workRegionId ? 'error' : 'default'}
          supportingText={errors.workRegionId?.message}
        />
        <Selector
          label={translate('addressForm.commune')}
          value={workCommuneId ? String(workCommuneId) : undefined}
          onChange={(value) => {
            const numericValue = normalizeSelectToNumber(value);
            setValue('workCommuneId', numericValue || null, { shouldDirty: true });
          }}
          options={workCommuneOptions}
          placeholder={translate('addressForm.communePlaceholder')}
          disabled={!workRegionId}
          color={errors.workCommuneId ? 'error' : 'default'}
          supportingText={errors.workCommuneId?.message}
        />

        {/* Row 2: Dirección (spans 4 columns) */}
        <LabelInput
          className="md:col-span-4"
          label={translate('addressForm.address')}
          registration={register('workAddress')}
          color={errors.workAddress ? 'error' : undefined}
          supportingText={errors.workAddress?.message}
        />
      </FormLayout.Section>

      <FormLayout.Footer
        cancelButton={
          <PrimaryButton type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </PrimaryButton>
        }
        primaryButton={
          <PermissionGuard action="edit">
            <PrimaryButton type="submit" isLoading={isSubmitting}>
              Guardar
            </PrimaryButton>
          </PermissionGuard>
        }
      />
    </FormLayout>
  );
};

export default EmployeeProfileAddressDataForm;
